/**
 * useGDrivePicker hook
 *
 * Loads the Google Picker SDK on first invocation, obtains a short-lived
 * OAuth access token via Google Identity Services, then opens the Picker UI.
 *
 * Mime-type restrictions follow the Phase 4 spec:
 *   - image/png, image/jpeg, image/webp, image/gif
 *   - video/mp4, video/quicktime
 *   - application/pdf
 * Google Workspace native types (Docs/Sheets/Slides) are intentionally excluded
 * (Risk #18 in GOOGLE-DRIVE-INTEGRATION.md).
 *
 * The caller supplies an onSelect callback that receives GDrivePickerFile[].
 */

import { useState, useCallback, useRef } from 'react';
import { loadPickerSdk } from '@/utils/gdrivePickerSdk';
import type { GDrivePickerFile, GDriveSharedFolder } from '@/types/gdrive';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

const DEFAULT_MIME_TYPES =
  'image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,application/pdf';

// Session-scoped token cache. Survives component remounts within the same
// browser tab. GIS access tokens last ~1h; we record expiry to skip stale ones.
const TOKEN_CACHE_KEY = 'gdrive_picker_token';

interface CachedToken {
  accessToken: string;
  expiresAt: number; // ms epoch
}

function readCachedToken(): string | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedToken;
    // 60s safety margin — don't return tokens about to expire mid-flow
    if (parsed.expiresAt > Date.now() + 60_000) {
      return parsed.accessToken;
    }
    sessionStorage.removeItem(TOKEN_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

function writeCachedToken(accessToken: string, expiresInSec: number): void {
  try {
    const payload: CachedToken = {
      accessToken,
      expiresAt: Date.now() + expiresInSec * 1000,
    };
    sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage unavailable or full — silent fallback to non-cached flow
  }
}

function clearCachedToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_CACHE_KEY);
  } catch {
    // ignore
  }
}

interface UseGDrivePickerOptions {
  /** The channel ID whose OAuth token will be used to open the Picker. */
  channelId: number;
  /** Called when the user confirms their file (or folder) selection. */
  onSelect: (files: GDrivePickerFile[]) => void;
  /** Called when the user cancels the Picker (optional). */
  onCancel?: () => void;
  /**
   * Comma-separated or array of accepted MIME types. Ignored in `'folders'` mode.
   * Defaults to image/*, video/mp4, video/quicktime, application/pdf.
   */
  acceptMimeTypes?: string[];
  /**
   * `'files'` (default) — browse for files to import. Locked to
   * `allowedFolders` (SPEC §5.2): one `DocsView.setParent(folderId)` per
   * shared folder, no all-Drive view, no upload tab. Unless `unrestricted`
   * is set — see below.
   *
   * `'folders'` — folder-only multi-select picker used for the connect-time
   * folder-selection step (§5.1) and the "Shared folders" manage editor
   * (§5.3). Ignores `acceptMimeTypes` / `allowedFolders` / `unrestricted`.
   */
  mode?: 'files' | 'folders';
  /**
   * Required (non-empty) when `mode === 'files'` and `unrestricted` is not
   * set. The channel's currently configured shared folders. If empty,
   * `open()` sets `error` and does not open the Picker — callers must run
   * the folder-selection step first (an UNCONFIGURED channel fails closed
   * server-side regardless).
   *
   * NAV_HIDDEN nuance (Picker docs): that feature restricts the UI to only
   * the FIRST added view, so it is only enabled when there is exactly ONE
   * allowed folder. With multiple folders we add one view per folder WITHOUT
   * NAV_HIDDEN so all of them stay reachable as tabs — the real security
   * boundary is the server-side gate, not this UI lock.
   */
  allowedFolders?: GDriveSharedFolder[];
  /**
   * `mode === 'files'` only. When `true`, the channel is access-mode `'all'`
   * (whole Drive, ACCESS-MODE extension) — the Picker opens the ORIGINAL
   * pre-scoping view set instead of the folder-locked `setParent` views:
   * one all-Drive `DOCS` view (respecting `acceptMimeTypes`) plus the
   * built-in `DOCS_IMAGES` / `DOCS_VIDEOS` / `PDFS` tabs and an upload tab.
   * `allowedFolders` is ignored (and may be empty) when this is set.
   */
  unrestricted?: boolean;
}

interface UseGDrivePickerReturn {
  /** Trigger the Picker flow. Loads SDK if not already loaded. */
  open: () => Promise<void>;
  /** True once the SDK has been successfully loaded. */
  isReady: boolean;
  /** Non-null when SDK loading or token acquisition failed. */
  error: Error | null;
}

export function useGDrivePicker({
  onSelect,
  onCancel,
  acceptMimeTypes,
  mode = 'files',
  allowedFolders,
  unrestricted = false,
}: UseGDrivePickerOptions): UseGDrivePickerReturn {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Cached access token — GIS reuses a valid token automatically
  const cachedTokenRef = useRef<string | null>(null);
  // Prevent double-open
  const isOpeningRef = useRef(false);

  const mimeTypes =
    acceptMimeTypes && acceptMimeTypes.length > 0
      ? acceptMimeTypes.join(',')
      : DEFAULT_MIME_TYPES;

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const developerKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY as string | undefined;

  /**
   * Opens the Google Picker after ensuring the SDK is loaded and an access token
   * is available. Deduplicates concurrent calls.
   */
  const open = useCallback(async (): Promise<void> => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;
    setError(null);

    // Declared here so the catch block can call it even if the try block
    // throws before (or during) the scroll-lock section.
    let restorePageScroll: (() => void) | null = null;

    try {
      // 1. Lazy-load SDK
      await loadPickerSdk();
      setIsReady(true);

      if (!clientId || !developerKey) {
        throw new Error(
          'Google Picker configuration is incomplete. ' +
          'VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_PICKER_API_KEY must be set.',
        );
      }

      // File-browsing mode requires at least one shared folder to lock the
      // Picker to (SPEC §5.2), UNLESS the channel is access-mode `'all'`
      // (`unrestricted`), which needs no folders at all. An UNCONFIGURED
      // channel fails closed on the server regardless, but there's no point
      // opening an OAuth popup for a Picker we can't build — bail early with
      // a clear error instead.
      if (mode === 'files' && !unrestricted && (!allowedFolders || allowedFolders.length === 0)) {
        throw new Error(
          'This Google Drive connection has no shared folders configured yet.',
        );
      }

      // 2. Obtain (or reuse) an access token via GIS popup
      // Skip the GIS round-trip entirely if a fresh token is in sessionStorage.
      const cached = readCachedToken();
      if (cached) {
        cachedTokenRef.current = cached;
      }

      const accessToken = await new Promise<string>((resolve, reject) => {
        if (cachedTokenRef.current) {
          resolve(cachedTokenRef.current);
          return;
        }

        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'https://www.googleapis.com/auth/drive.file',
          // GIS defaults include_granted_scopes=true, which merges the user's
          // previously granted YouTube scopes (same client_id) into this request
          // and Google rejects drive.file + youtube.* together (invalid_request).
          include_granted_scopes: false,
          callback: (tokenResponse) => {
            if (tokenResponse.error) {
              reject(
                new Error(
                  `Google OAuth error: ${tokenResponse.error}`,
                ),
              );
              return;
            }
            cachedTokenRef.current = tokenResponse.access_token;
            // GIS expires_in is in seconds; default ~3600
            const expiresIn = Number(tokenResponse.expires_in) || 3600;
            writeCachedToken(tokenResponse.access_token, expiresIn);
            resolve(tokenResponse.access_token);
          },
          error_callback: (err) => {
            reject(new Error(`Google OAuth error: ${err.type}`));
          },
        });

        // prompt:'' = silent grant when scope already consented in this browser.
        // Default ('consent') would re-show the consent UI on every click.
        tokenClient.requestAccessToken({ prompt: '' });
      });

      // 3. Build and show the Picker — shape matches Google's official sample
      // exactly to minimise surface for "Invalid data" errors from the Picker
      // bundle. Don't add setOrigin / setTitle / setMaxItems unless verified
      // to work in this exact runtime.
      const ViewId = window.google.picker.ViewId;
      const Feature = window.google.picker.Feature;

      // Views + features differ by mode:
      //   'folders' — a single all-folders view with select-folder enabled,
      //     used for the connect-time folder-selection step and the "Shared
      //     folders" manage editor. Multi-select so the user can pick several
      //     folders (e.g. nested Bilder/Videos/Blog subfolders) in one go.
      //   'files' + unrestricted (access-mode 'all') — the ORIGINAL
      //     pre-scoping view set: one all-Drive DOCS view (respecting
      //     `acceptMimeTypes`) plus the built-in DOCS_IMAGES / DOCS_VIDEOS /
      //     PDFS tabs and an upload tab. No `setParent` — nothing to lock to,
      //     the whole Drive is in scope.
      //   'files' scoped — one DocsView.setParent(folderId) per shared
      //     folder, no all-Drive view, no upload tab (SPEC §5.2). NAV_HIDDEN
      //     (locks the Picker UI to only the first view) is only safe with
      //     exactly one folder — with multiple folders each stays reachable
      //     as its own tab instead. The server-side gate is the real
      //     security boundary either way; this is UX scoping only.
      //
      // Known Picker limitation (investigated, no fix applied — see
      // useGDriveImport.ts's friendlyGDriveImportErrorMessage doc comment):
      // setParent() only scopes BROWSING within a view. The Picker's own
      // search box always queries the whole Drive regardless of the active
      // view's parent, and there is no documented Feature/DocsView setting
      // to scope or hide search (checked google.picker.Feature — MINE_ONLY,
      // MULTISELECT_ENABLED, NAV_HIDDEN, SIMPLE_UPLOAD_ENABLED,
      // SUPPORT_DRIVES; none affect search). This is UX-only exposure — the
      // server rejects out-of-scope picks with GDRIVE_FOLDER_NOT_ALLOWED
      // regardless of how the Doc/file was found in the Picker.
      const views: GooglePickerView[] = [];
      const features: string[] = [Feature.MULTISELECT_ENABLED];

      if (mode === 'folders') {
        const folderView = new window.google.picker.DocsView(ViewId.FOLDERS)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(true);
        views.push(folderView);
      } else if (unrestricted) {
        const allDriveView = new window.google.picker.View(ViewId.DOCS);
        allDriveView.setMimeTypes(mimeTypes);
        const imagesView = new window.google.picker.View(ViewId.DOCS_IMAGES);
        const videosView = new window.google.picker.View(ViewId.DOCS_VIDEOS);
        const pdfsView = new window.google.picker.View(ViewId.PDFS);
        const uploadView = new window.google.picker.DocsUploadView();
        views.push(allDriveView, imagesView, videosView, pdfsView, uploadView);
      } else {
        // mode === 'files', scoped — allowedFolders is guaranteed non-empty here (checked above)
        const folders = allowedFolders!;
        for (const folder of folders) {
          const folderScopedView = new window.google.picker.DocsView(ViewId.DOCS)
            .setParent(folder.id)
            .setMimeTypes(mimeTypes);
          views.push(folderScopedView);
        }
        if (folders.length === 1) {
          features.push(Feature.NAV_HIDDEN);
        }
      }

      // appId = GCP project number = leading numeric segment of OAuth client ID
      // before the first hyphen. e.g. for "833224151034-abc.apps...", appId is
      // "833224151034".
      const appId = clientId.split('-')[0];
      if (!appId || !/^\d+$/.test(appId)) {
        throw new Error(
          'VITE_GOOGLE_CLIENT_ID format is unexpected — cannot extract GCP project number for Picker appId.',
        );
      }

      // Lock body scroll while Picker is open. Picker's iframe takes focus on
      // load and the browser auto-scrolls the page to the iframe — locking
      // overflow:hidden on body keeps the page anchored.
      const previousScrollY = window.scrollY;

      // Neutralize scroll BEFORE the Picker measures. Google's Picker
      // positions its `.picker-dialog` (position:fixed) using the page's
      // scroll offset at `setVisible(true)` time — on a long, scrolled page
      // (e.g. Channel Settings scrolled down to a GDrive channel row) the
      // dialog was placed `window.scrollY` px below the viewport, rendering
      // fully (long page) or partially (short page) off-screen. Scrolling to
      // top FIRST — before the overflow:hidden lock, which can otherwise
      // freeze the scroll position before it takes effect — makes the
      // Picker measure from scrollTop 0 and center correctly.
      // `restorePageScroll` below still returns the user to
      // `previousScrollY` on close.
      window.scrollTo(0, 0);
      const unlockScroll = lockBodyScroll();

      // Make the rest of the page inert while Picker is up. The composer modal
      // (Radix-style) intercepts pointer events at the document level for its
      // focus trap and outside-click handlers, which steals clicks from Picker
      // even when Picker has higher z-index. Adding `gdrive-picker-active` to
      // body lets the global CSS in index.css set pointer-events: none on
      // everything except the Picker DOM, sidestepping the trap entirely.
      document.body.classList.add('gdrive-picker-active');

      // Assign to the outer-scope variable so the catch block can also call it.
      restorePageScroll = () => {
        unlockScroll();
        document.body.classList.remove('gdrive-picker-active');
        window.scrollTo(0, previousScrollY);
      };

      // Belt-and-suspenders: force the Picker to recompute its position against
      // the current viewport. Even after scrolling to top, the Picker can
      // measure before layout settles and land off-screen; a `resize` event
      // triggers its internal reposition (the exact effect of opening DevTools,
      // which is how this bug was diagnosed). The Picker mounts its iframe
      // asynchronously, so nudge it via Action.LOADED (the reliable signal that
      // the iframe finished loading) plus a next-frame/short/longer fallback
      // for slow first-loads (e.g. right after the GIS sign-in popup, where the
      // iframe can mount later than the fallback timers). Guarded on the
      // `gdrive-picker-active` body class so it no-ops if the Picker was
      // already closed/disposed before the timer fires.
      const repositionPicker = (): void => {
        if (document.body.classList.contains('gdrive-picker-active')) {
          window.dispatchEvent(new Event('resize'));
        }
      };

      let pickerBuilder = new window.google.picker.PickerBuilder()
        .setDeveloperKey(developerKey)
        .setAppId(appId)
        .setOAuthToken(accessToken);

      for (const feature of features) {
        pickerBuilder = pickerBuilder.enableFeature(feature);
      }
      for (const view of views) {
        pickerBuilder = pickerBuilder.addView(view);
      }

      const picker = pickerBuilder
        .setCallback((data: GooglePickerResponse) => {
          // Picker fires Action.LOADED when it first opens — that's NOT a
          // terminal event and must not dispose. Only PICKED and CANCEL are
          // terminal. Disposing on LOADED self-destructs the picker on open.
          // LOADED IS however the reliable signal that the iframe has finished
          // mounting and laid out, so use it to trigger the reposition nudge
          // (fixes the first-open-after-GIS-popup mispositioning race, where
          // the iframe mounts later than the requestAnimationFrame/setTimeout
          // fallbacks below).
          if (data.action === window.google.picker.Action.LOADED) {
            repositionPicker();
          } else if (data.action === window.google.picker.Action.PICKED) {
            const files: GDrivePickerFile[] = data.docs.map((doc) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              sizeBytes: doc.sizeBytes,
              iconUrl: doc.iconUrl || undefined,
            }));
            onSelect(files);
            picker.dispose();
            restorePageScroll();
          } else if (data.action === window.google.picker.Action.CANCEL) {
            onCancel?.();
            picker.dispose();
            restorePageScroll();
          }
        })
        .build();

      picker.setVisible(true);

      // Fallback nudges in addition to the Action.LOADED-triggered reposition
      // above — next frame, a short timeout, and a longer one to cover slow
      // first-loads (e.g. right after the GIS sign-in popup).
      requestAnimationFrame(repositionPicker);
      window.setTimeout(repositionPicker, 250);
      window.setTimeout(repositionPicker, 600);
    } catch (err) {
      restorePageScroll?.(); // always clean up scroll lock on any error path
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      // Invalidate cached token on auth errors so we re-request next time
      if (e.message.includes('OAuth') || e.message.includes('token')) {
        cachedTokenRef.current = null;
        clearCachedToken();
      }
    } finally {
      isOpeningRef.current = false;
    }
  }, [clientId, developerKey, mimeTypes, onSelect, onCancel, mode, allowedFolders, unrestricted]);

  return { open, isReady, error };
}
